import { useState } from 'react';
import ClaimIdentityForm from '../components/ClaimIdentityForm';
import IsConnectedWrapper from '../components/IsConnectedWrapper';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertTriangle,
  Download,
  FileText,
  ExternalLink,
  Play,
} from 'lucide-react';

// Step component for the how-to guide
function StepItem({
  step,
  title,
  description,
  children,
}: {
  step: number;
  title: string;
  description: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex gap-4">
      <div className="w-8 h-8 rounded-full bg-brand flex items-center justify-center shrink-0 text-white text-sm font-semibold">
        {step}
      </div>
      <div className="flex-1 min-w-0 pb-4">
        <h4 className="text-sm font-semibold text-text-default mb-1">
          {title}
        </h4>
        <div className="text-sm text-text-secondary">{description}</div>
        {children}
      </div>
    </div>
  );
}

// How to register guide component
function HowToRegisterGuide({ onClose }: { onClose: () => void }) {
  return (
    <div className="space-y-5">
      {/* Steps */}
      <div className="space-y-1">
        <StepItem
          step={1}
          title="Download Shinkai Desktop"
          description={
            <>
              Visit our website and download the Shinkai Desktop App for your
              operating system.
            </>
          }
        >
          <a
            href="https://www.shinkai.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-brand hover:underline mt-2"
          >
            <Download className="w-3.5 h-3.5" />
            Download Shinkai Desktop
            <ExternalLink className="w-3 h-3" />
          </a>
        </StepItem>

        <StepItem
          step={2}
          title="Get Your Keys from Shinkai Desktop"
          description={
            <>
              Once installed, go to <strong>Settings → General</strong>, and
              click on "Register Your Shinkai Identity". All configurations will
              be set up automatically.
            </>
          }
        />

        <StepItem
          step={3}
          title="Fill Out the Registration Form"
          description={
            <>
              Enter your Shinkai identity name and click "Register Identity".{' '}
              <a
                href="https://docs.shinkai.com/advanced/shinkai-identity"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand hover:underline"
              >
                Learn more in the docs
              </a>
              .
            </>
          }
        />
      </div>

      {/* Video Demo */}
      <div className="rounded-xl overflow-hidden border border-divider bg-bg-tertiary/30">
        <div className="aspect-video relative">
          <video
            src="/demo-identity.mp4"
            autoPlay
            muted
            loop
            controls
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>
      </div>

      {/* Close button */}
      <Button
        variant="outline"
        onClick={onClose}
        className="w-full border-divider"
      >
        Got it, let me register
      </Button>
    </div>
  );
}

export default function RegisterIdentityPage() {
  const [isHowToOpen, setIsHowToOpen] = useState(false);
  const queryParameters = new URLSearchParams(window.location.search);
  const encryptionPublicKeyFromUrl = queryParameters.get('encryption_pk') ?? '';
  const signaturePublicKeyFromUrl = queryParameters.get('signature_pk') ?? '';

  const showWarning = !encryptionPublicKeyFromUrl || !signaturePublicKeyFromUrl;

  return (
    <main className="grow pb-20 px-6 font-inter">
      <div className="max-w-2xl mx-auto space-y-6">
        <IsConnectedWrapper>
          {/* Header */}
          <section className="text-center space-y-3">
            <h1 className="font-clash text-4xl font-medium text-text-default tracking-tight">
              Register Identity
            </h1>
            <p className="text-base text-text-secondary max-w-md mx-auto">
              Create your unique Shinkai identity to participate in the
              ecosystem and earn rewards.
            </p>
          </section>

          {/* Warning Banner */}
          {showWarning && (
            <div className="rounded-2xl border border-yellow-500/30 bg-yellow-500/10 p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-yellow-500/20 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-5 h-5 text-yellow-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-yellow-400 mb-1">
                    Important: Use Shinkai Desktop App
                  </h3>
                  <p className="text-sm text-text-secondary mb-3">
                    For the best experience, register your identity through the
                    Shinkai Desktop App. It will automatically configure your
                    keys.
                  </p>
                  <Button
                    variant="outline"
                    size="xs"
                    onClick={() => setIsHowToOpen(true)}
                    className="gap-2 border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
                  >
                    <Play className="w-3.5 h-3.5" />
                    See how it works
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Registration Form Card */}
          <div className="rounded-2xl border border-divider bg-bg-secondary/20 overflow-hidden">
            {/* Card Header */}
            <div className="px-6 py-4 border-b border-divider bg-bg-tertiary/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-brand/10 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-brand" />
                </div>
                <div>
                  <h2 className="font-medium text-text-default">
                    Registration Form
                  </h2>
                  <p className="text-xs text-text-tertiary">
                    Fill in your details to claim your identity
                  </p>
                </div>
              </div>
            </div>

            {/* Form Content */}
            <div className="p-6">
              <ClaimIdentityForm />
            </div>
          </div>

          {/* Help Link */}
          <div className="text-center">
            <button
              type="button"
              onClick={() => setIsHowToOpen(true)}
              className="text-sm text-text-tertiary hover:text-text-default transition-colors"
            >
              Need help? View the registration guide
            </button>
          </div>

          {/* How-To Dialog */}
          <Dialog open={isHowToOpen} onOpenChange={setIsHowToOpen}>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Before You Register</DialogTitle>
                <DialogDescription>
                  Follow these steps to ensure you have the correct keys
                </DialogDescription>
              </DialogHeader>
              <div className="pt-4">
                <HowToRegisterGuide onClose={() => setIsHowToOpen(false)} />
              </div>
            </DialogContent>
          </Dialog>
        </IsConnectedWrapper>
      </div>
    </main>
  );
}
